import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { Button } from '@/components/buttons/Button';
import { isEmpty } from '@/shared/utility';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthToken } from '@/store/selectors/authSelectors';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import BffApiService from '../../../shared/bffApiService';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { getUser, getUserTags } from '@/store/actions/user';
import Form from '@/components/form/Form';
import TextField from '@/components/form/text/TextField';
import NumericField from '@/components/form/alpha-numeric/NumericField';
import SearchDropdownField from '@/components/form/search-dropdown/SearchDropdownField';
import PhoneNumberField from '@/components/form/phone-number/PhoneNumberField';
import AlphaNumericField from '@/components/form/alpha-numeric/AlphaNumericField';
import { BffSubdivisionV2 } from '@lucky7ventures/bff-types';

interface MexicoVerificationForm {
  address: string;
  postCode: string;
  city: string;
  state: string;
  curp: string;
  phone: {
    country: string;
    number: string;
    prefix: string;
    wholeNumber: string;
  };
}

const initialValues: MexicoVerificationForm = {
  address: '',
  postCode: '',
  city: '',
  state: '',
  curp: '',
  phone: { prefix: '', number: '', country: '', wholeNumber: '' },
};

const CompleteProfileMexicoCurp = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const token = useSelector(selectAuthToken);
  const {
    request: postCompleteProfile,
    loading: completeProfileLoading,
    error: completeProfileError,
  } = useBffApiRequest();
  const { request: subdivisionsRequest } = useBffApiRequest<BffSubdivisionV2[]>();
  const [subdivisions, setSubdivisions] = useState<{ value: string; label: string }[]>([]);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    subdivisionsRequest({
      apiMethod: BffApiService.getSubdivisions,
      pathParams: { countryCode: 'MX' },
      successCallback: data => {
        const mappedSubdivisions = data.map(subdivision => ({
          value: subdivision.code,
          label: subdivision.name,
        }));
        setSubdivisions(mappedSubdivisions);
      },
    });
  }, []);

  const validationSchema = Yup.object().shape({
    address: Yup.string().required(intl.formatMessage({ id: 'inputs.address.required' })),
    postCode: Yup.string()
      .required(intl.formatMessage({ id: 'inputs.postcode.required' }))
      .matches(/^\d{5}$/, intl.formatMessage({ id: 'inputs.postcode.format' })),
    city: Yup.string().required(intl.formatMessage({ id: 'inputs.city.required' })),
    state: Yup.string().required(intl.formatMessage({ id: 'inputs.state.required' })),
    phone: Yup.object().shape({
      prefix: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
      number: Yup.string()
        .required(intl.formatMessage({ id: 'input.error.required' }))
        .min(10, intl.formatMessage({ id: 'inputs.mobile.invalid' })),
    }),
    curp: Yup.string()
      .required(intl.formatMessage({ id: 'inputs.curp.required' }))
      .matches(
        /^[A-Z]{1}[AEIOUX]{1}[A-Z]{2}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])[HM](AS|BC|BS|CC|CL|CM|CS|CH|DF|DG|GT|GR|HG|JC|MC|MN|MS|NT|NL|OC|PL|QT|QR|SP|SL|SR|TC|TL|TS|VZ|YN|ZS|NE)[B-DF-HJ-NP-TV-Z]{3}[A-Z0-9]{1}[0-9]{1}$/,
        intl.formatMessage({ id: 'inputs.curp.invalid-format' }),
      ),
  });

  const handleOnSubmit = ({
    address,
    city,
    phone,
    postCode,
    state,
    curp,
  }: MexicoVerificationForm) => {
    postCompleteProfile({
      apiMethod: BffApiService.completeProfileMexicoCurp,
      payload: {
        address,
        city,
        postCode,
        phone: { prefix: phone.prefix, number: phone.number },
        state,
        curp,
      },
      successCallback: () => {
        dispatch(getUser(token));
        dispatch(getUserTags());
        setIsSuccess(true);
        triggerGTag(GTagEvents.complete_profile_success);
      },
    });
  };

  return (
    <div>
      {isSuccess ? (
        <div className="mx-auto max-w-[520px] md:p-8 bg-white md:rounded-xl md:drop-shadow-xl">
          <h2 className="text-green-500 text-[26px] md:text-3xl text-center m-0">
            {intl.formatMessage({ id: 'completeProfile.success.header' })}
          </h2>
          <p className="md:text-center mt-6 mx-0 mb-10">
            {intl.formatMessage({ id: 'completeProfile.success.text' })}
          </p>
          <Button
            type="button"
            text={intl.formatMessage({ id: 'misc.continue' })}
            onClickHandler={() => dispatch(getUserTags())}
          />
        </div>
      ) : (
        <Form
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnBlur
          onSubmit={handleOnSubmit}
        >
          {({ values }) => (
            <div className="flex flex-col space-y-12 items-center w-full">
              <div className="flex flex-col space-y-6 w-full max-w-[520px] md:bg-white md:drop-shadow-xl md:px-8 md:py-12 rounded-xl">
                <div className="pb-6">
                  <h2 className="md:text-center m-0">
                    {intl.formatMessage({ id: 'completeProfile.prompt.header' })}
                  </h2>
                  <p className="md:text-center mt-3 m-0">
                    {intl.formatMessage({ id: 'completeProfile.prompt.text' })}
                  </p>
                </div>
                <TextField
                  data-cy="mexico-verify-address"
                  className="border-[1px] border-gray-300"
                  labelClassName="text-blue-blue"
                  name="address"
                  label={intl.formatMessage({ id: 'inputs.address' })}
                />
                <NumericField
                  data-cy="mexico-verify-postCode"
                  className="border-[1px] border-gray-300"
                  labelClassName="text-blue-blue"
                  name="postCode"
                  label={intl.formatMessage({ id: 'inputs.postcode' })}
                />
                <TextField
                  data-cy="mexico-verify-city"
                  className="border-[1px] border-gray-300"
                  labelClassName="text-blue-blue"
                  name="city"
                  label={intl.formatMessage({ id: 'inputs.city' })}
                />
                <SearchDropdownField
                  data-cy="mexico-verify-state"
                  inputClassName="border-[1px] focus:border-gray-300 border-gray-300"
                  labelClassName="text-blue-blue"
                  name="state"
                  options={subdivisions}
                  optionsClassName="border-[1px] border-gray-300"
                  label={intl.formatMessage({ id: 'inputs.state' })}
                />
                <PhoneNumberField
                  country="mx"
                  label={intl.formatMessage({ id: 'inputs.mobile' })}
                  className="border-[1px] border-gray-300"
                  labelClassName="text-blue-blue"
                  name="phone"
                />
                <AlphaNumericField
                  data-cy="mexico-verify-curp"
                  name="curp"
                  label={intl.formatMessage({ id: 'inputs.curp' })}
                  className="border-[1px] border-gray-300"
                  labelClassName="text-blue-blue"
                />
                {completeProfileError && (
                  <p className="text-red-light text-md">
                    {intl.formatMessage({ id: 'error.support' })}
                  </p>
                )}
                <div className="mx-auto w-full max-w-[200px] pt-6">
                  <Button
                    disabled={
                      !isEmpty(Object.values(values).filter(value => isEmpty(value))) ||
                      completeProfileLoading
                    }
                    type="submit"
                    text={intl.formatMessage({ id: 'misc.submit' })}
                  />
                </div>
              </div>
            </div>
          )}
        </Form>
      )}
    </div>
  );
};

export default CompleteProfileMexicoCurp;
